import React, { useState, useEffect, useCallback } from 'react';
import Chart from 'react-apexcharts';
import { chartColors } from '../data/dashboardData';
import { dashboardAPI } from '../services/apiClient';
import { appendDateParams } from '../utils/dateParams';

const StorePerformance = ({ dateRange, onRegisterDrillDownTrigger }) => {
  // State for retailer validation data (real API data)
  const [retailers, setRetailers] = useState([]);
  const [retailersLoading, setRetailersLoading] = useState(false);
  const [retailersError, setRetailersError] = useState(false);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    total: 0,
    pageCount: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [storeSearch, setStoreSearch] = useState('');
  const [storeSearchQuery, setStoreSearchQuery] = useState('');

  // State for sorting
  const [sortBy, setSortBy] = useState('accuracy');
  const [sortDirection, setSortDirection] = useState('desc');

  // State for top and worst performing stores (separate API calls)
  const [topPerformingStores, setTopPerformingStores] = useState([]);
  const [worstPerformingStores, setWorstPerformingStores] = useState([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceError, setPerformanceError] = useState(false);

  // State for drill down dialog
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);

  // State for coupon redemptions in drill down
  const [couponRedemptions, setCouponRedemptions] = useState([]);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(false);
  const [couponPagination, setCouponPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    total: 0,
    pageCount: 0
  });
  const [couponCurrentPage, setCouponCurrentPage] = useState(1);
  const [couponPageSize, setCouponPageSize] = useState(10);
  const [revalidatingCouponId, setRevalidatingCouponId] = useState(null);

  // State for basket details popup
  const [basketDetailsOpen, setBasketDetailsOpen] = useState(false);
  const [selectedBasket, setSelectedBasket] = useState(null);

  // State for invoice preview
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // State for image controls
  const [imageRotation, setImageRotation] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePanX, setImagePanX] = useState(0);
  const [imagePanY, setImagePanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // State for drilldown tabs
  const [activeStoreTab, setActiveStoreTab] = useState('performance');

  // State for status filtering in drill down
  const [statusFilter, setStatusFilter] = useState(null);
  // Derived reason filter (below min spend, exceeds max discount, no HILLS)
  const [derivedFilter, setDerivedFilter] = useState(null);

  // Helper function to build query string with date, pagination, and sort parameters
  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    const skip = (currentPage - 1) * pageSize;
    params.append('Skip', skip.toString());
    params.append('Take', pageSize.toString());
    
    appendDateParams(params, dateRange);
    
    // Add sorting parameters
    params.append('SortBy', sortBy);
    params.append('SortDirection', sortDirection);
    if (storeSearchQuery) {
      params.append('Search', storeSearchQuery);
    }
    
    return `?${params.toString()}`;
  }, [currentPage, pageSize, dateRange, sortBy, sortDirection, storeSearchQuery]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      setStoreSearchQuery(storeSearch.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [storeSearch]);

  // Handle sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // If clicking the same column, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different column, set new column and default to desc
      setSortBy(column);
      setSortDirection('desc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  // Get sort indicator for table headers
  const getSortIndicator = (column) => {
    if (sortBy !== column) {
      return ' ↕️'; // Both arrows when not sorted by this column
    }
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // Helper function to apply VAT correction and calculate status (reusable)
  const applyVATCorrectionAndCalculateStatus = useCallback((item) => {
    let calculatedDiscount = item.calculatedDiscount;
    const discountApplied = item.discountApplied || 0;
    const detectedDiscount = item.detectedDiscount || 0;
    
    // If calculatedDiscount is missing or null, use the coupon amount as the expected value
    if (calculatedDiscount === null || calculatedDiscount === undefined) {
      calculatedDiscount = item.amount || 0;
    }

    // Treat null/missing values as 0
    const discountClaimed = discountApplied || 0;
    let estimatedDue = calculatedDiscount || 0;

    // VAT correction heuristic: if AI estimate excl. VAT times 1.15 is within R10 of claimed,
    // assume data typo and align AI estimate to claimed for UI purposes
    if (estimatedDue > 0 && discountClaimed > 0) {
      const vatAdjustedEstimate = estimatedDue * 1.15;
      if (Math.abs(vatAdjustedEstimate - discountClaimed) <= 10.0) {
        calculatedDiscount = discountApplied;
        estimatedDue = calculatedDiscount;
      }
    }

    // VAT correction heuristic for detected_discount: if AI detected excl. VAT times 1.15 is within R10 of claimed,
    // assume data typo and align AI detected to claimed for UI purposes
    let adjustedDetectedDiscount = detectedDiscount || 0;
    if (adjustedDetectedDiscount > 0 && discountClaimed > 0) {
      const vatAdjustedDetected = adjustedDetectedDiscount * 1.15;
      if (Math.abs(vatAdjustedDetected - discountClaimed) <= 10.0) {
        adjustedDetectedDiscount = discountApplied;
      }
    }

    // Calculate status based on business logic (5 rand tolerance)
    let status = 'Correct';
    const actuallyGiven = adjustedDetectedDiscount;
    
    // Check for invalid transactions first
    if (!calculatedDiscount && discountApplied === 0) {
      status = 'Invalid';
    } else {
      // Primary check: Claimed vs Actually Given (5 rand tolerance)
      const claimedVsGivenDiff = Math.abs(discountClaimed - actuallyGiven);
      if (claimedVsGivenDiff > 5.00) {
        if (discountClaimed > actuallyGiven) {
          status = 'Under Paid';  // Vet claimed more than actually given - customer underpaid
        } else {
          status = 'Over Paid';   // Vet claimed less than actually given - customer overpaid
        }
      } else {
        // Secondary check: AI Estimate vs Claimed (5 rand tolerance)
        const estimateVsClaimedDiff = Math.abs(estimatedDue - discountClaimed);
        if (estimateVsClaimedDiff > 5.00) {
          if (estimatedDue > discountClaimed) {
            status = 'Discount Calc Too Low';   // AI thinks should be higher
          } else {
            status = 'Discount Calc Too High';  // AI thinks should be lower
          }
        } else {
          status = 'Correct';  // Everything matches (green tick)
        }
      }
    }

    return { status, adjustedDetectedDiscount, calculatedDiscount };
  }, []);

  // Fetch retailer validation data
  const fetchRetailers = useCallback(async () => {
    setRetailersLoading(true);
    setRetailersError(false);
    try {
      const queryString = buildQueryString();
      const response = await dashboardAPI.getTopRetailersByAccuracy(queryString);
      
      const result = response.data;
      
      // Get list of retailers from API
      const retailerList = result.items || result || [];
      
      // PERFORMANCE WARNING: Recalculating statistics client-side with VAT correction
      // This fetches ALL coupon redemptions for ALL stores, which can be slow/timeout
      // For better performance, consider fixing this server-side in the API
      // This ensures accurate statistics by applying VAT correction before counting
      const retailerData = await Promise.all(retailerList.map(async (item) => {
        const retailerName = item.retailer || 'Unknown';
        
        try {
          // Fetch coupon redemptions for this retailer (limited to avoid timeout)
          const couponResponse = await dashboardAPI.getCouponRedemptions(
            retailerName,
            0,
            10000, // Get up to 10k records
            'redeemedAt',
            'desc',
            '',
            false,
            dateRange
          );
          
          const couponItems = couponResponse.data?.items || [];
          
          // Apply VAT correction and recalculate statistics
          let validCount = 0;
          let overPaidCount = 0;
          let underPaidCount = 0;
          
          couponItems.forEach(coupon => {
            const { status } = applyVATCorrectionAndCalculateStatus(coupon);
            if (status === 'Correct') {
              validCount++;
            } else if (status === 'Over Paid') {
              overPaidCount++;
            } else if (status === 'Under Paid') {
              underPaidCount++;
            }
          });
          
          const totalCount = couponItems.length || item.totalCount || 0;
          const accuracy = totalCount > 0 ? (validCount / totalCount) * 100 : (item.accuracy || 0);
          
          return {
            name: retailerName,
            claims: totalCount,
            correct: validCount,
            accuracy: accuracy,
            overClaimed: overPaidCount,
            underPaid: underPaidCount,
            percentOfTotal: item.percentOfTotal || 0
          };
        } catch (error) {
          // If fetching fails, fall back to API values
          console.warn(`Failed to recalculate stats for ${retailerName}, using API values:`, error);
          return {
            name: retailerName,
            claims: item.totalCount || 0,
            correct: item.validCount || 0,
            accuracy: item.accuracy || 0,
            overClaimed: item.overPaidCount || 0,
            underPaid: item.underPaidCount || 0,
            percentOfTotal: item.percentOfTotal || 0
          };
        }
      }));
      
      setRetailers(retailerData);
      setPagination({
        pageNumber: result.pageNumber || currentPage,
        pageSize: result.pageSize || pageSize,
        total: result.total || 0,
        pageCount: result.pageCount || 0
      });
    } catch (error) {
      console.error('Error fetching retailers:', error);
      setRetailersError(true);
    } finally {
      setRetailersLoading(false);
    }
  }, [buildQueryString, currentPage, pageSize, dateRange, applyVATCorrectionAndCalculateStatus]);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pageCount) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Compute effective pagination when API does not paginate results server-side
  const serverPaginated = (pagination.pageCount || 0) > 0 && (pagination.total || 0) > retailers.length;
  const effectiveTotalStores = serverPaginated ? (pagination.total || retailers.length) : retailers.length;
  const effectivePageCount = serverPaginated ? (pagination.pageCount || 1) : Math.ceil(effectiveTotalStores / pageSize) || 1;
  const listStartIndex = (currentPage - 1) * pageSize;
  const listEndIndex = listStartIndex + pageSize;
  const paginatedRetailers = serverPaginated ? retailers : retailers.slice(listStartIndex, listEndIndex);

  // Fetch top and worst performing stores
  const fetchPerformanceData = useCallback(async () => {
    setPerformanceLoading(true);
    setPerformanceError(false);
    
    try {
      // Build base query parameters for performance data
      const baseParams = new URLSearchParams();
      baseParams.append('Skip', '0');
      baseParams.append('Take', '10');
      baseParams.append('SortBy', 'accuracy');
      
      appendDateParams(baseParams, dateRange);

      // Fetch top performers (highest accuracy first)
      const topParams = new URLSearchParams(baseParams);
      topParams.append('SortDirection', 'desc');
      const topResponse = await dashboardAPI.getTopRetailersByAccuracy(`?${topParams.toString()}`);
      
      // Fetch worst performers (lowest accuracy first)  
      const worstParams = new URLSearchParams(baseParams);
      worstParams.append('SortDirection', 'asc');
      const worstResponse = await dashboardAPI.getTopRetailersByAccuracy(`?${worstParams.toString()}`);

      const topResult = topResponse.data;
      const worstResult = worstResponse.data;

      // Map API response to chart format
      const topStores = (topResult.items || topResult || []).map(item => ({
        name: item.retailer || 'Unknown',
        accuracy: item.accuracy || 0
      }));

      const worstStores = (worstResult.items || worstResult || []).map(item => ({
        name: item.retailer || 'Unknown', 
        accuracy: item.accuracy || 0
      }));

      setTopPerformingStores(topStores);
      setWorstPerformingStores(worstStores);
    } catch (error) {
      console.error('Error fetching performance data:', error);
      setPerformanceError(true);
    } finally {
      setPerformanceLoading(false);
    }
  }, [dateRange]);

  // Data formatters
  const formatPercentage = (value) => `${value.toFixed(1)}%`;
  const formatNumber = (value) => value.toLocaleString();
  const formatCurrency = (value) => `R${(value ?? 0).toFixed(2)}`;
  const formatRelativeDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch {
      return new Date(dateStr).toLocaleString();
    }
  };

  // Fetch grid data when its filters, sorting, or pagination change
  useEffect(() => {
    fetchRetailers();
  }, [fetchRetailers]);

  // Performance charts only depend on the selected date range
  useEffect(() => {
    fetchPerformanceData();
  }, [fetchPerformanceData]);

  // handleDrillDown will be defined after fetchCouponRedemptions

  // Function to close drill down dialog
  const handleCloseDrillDown = () => {
    setDrillDownOpen(false);
    setSelectedStore(null);
    // Reset coupon data
    setCouponRedemptions([]);
    setCouponCurrentPage(1);
    setCouponPageSize(10);
    // Reset basket details
    setBasketDetailsOpen(false);
    setSelectedBasket(null);
    // Reset invoice preview
    setInvoicePreviewOpen(false);
    setSelectedInvoice(null);
  };

  // Fetch coupon redemptions for selected store
  const fetchCouponRedemptions = useCallback(async (storeName, options = {}) => {
    const silent = options.silent === true;
    if (!silent) {
      setCouponLoading(true);
    }
    setCouponError(false);
    
    try {
      // When drilling down into a store, always fetch complete dataset
      // Users expect full details when they specifically select a store to analyze
      const fetchTake = 10000; // Large number to get all items for this store
      const fetchSkip = 0; // Start from beginning
      
      const response = await dashboardAPI.getCouponRedemptions(
        storeName,
        fetchSkip,
        fetchTake,
        'redeemedAt', // orderBy
        'desc', // orderDirection - newest first
        '', // search
        false, // includeDeleted
        dateRange // dateRange parameter
      );
      
      const result = response.data;
      
      // Map API response to component format
      const couponData = (result.items || []).map(item => {
        // Calculate differences with better handling of missing values
        let calculatedDiscount = item.calculatedDiscount;
        const discountApplied = item.discountApplied || 0;
        const detectedDiscount = item.detectedDiscount || 0;
        
        // If calculatedDiscount is missing or null, use the coupon amount as the expected value
        if (calculatedDiscount === null || calculatedDiscount === undefined) {
          calculatedDiscount = item.amount || 0;
        }

        // Treat null/missing values as 0
        const discountClaimed = discountApplied || 0;
        let estimatedDue = calculatedDiscount || 0;

        // VAT correction heuristic: if AI estimate excl. VAT times 1.15 is within R10 of claimed,
        // assume data typo and align AI estimate to claimed for UI purposes
        if (estimatedDue > 0 && discountClaimed > 0) {
          const vatAdjustedEstimate = estimatedDue * 1.15;
          if (Math.abs(vatAdjustedEstimate - discountClaimed) <= 10.0) {
            calculatedDiscount = discountApplied;
            estimatedDue = calculatedDiscount;
          }
        }

        // VAT correction heuristic for detected_discount: if AI detected excl. VAT times 1.15 is within R10 of claimed,
        // assume data typo and align AI detected to claimed for UI purposes
        let adjustedDetectedDiscount = detectedDiscount || 0;
        if (adjustedDetectedDiscount > 0 && discountClaimed > 0) {
          const vatAdjustedDetected = adjustedDetectedDiscount * 1.15;
          if (Math.abs(vatAdjustedDetected - discountClaimed) <= 10.0) {
            adjustedDetectedDiscount = discountApplied;
          }
        }

        // Calculate status based on business logic (5 rand tolerance)
        let status = 'Correct';
        
        const actuallyGiven = adjustedDetectedDiscount;
        
        // Check for invalid transactions first
        if (!calculatedDiscount && discountApplied === 0) {
          status = 'Invalid';
        } else {
          // Primary check: Claimed vs Actually Given (5 rand tolerance)
          const claimedVsGivenDiff = Math.abs(discountClaimed - actuallyGiven);
          if (claimedVsGivenDiff > 5.00) {
            if (discountClaimed > actuallyGiven) {
              status = 'Under Paid';  // Vet claimed more than actually given - customer underpaid
            } else {
              status = 'Over Paid';   // Vet claimed less than actually given - customer overpaid
            }
          } else {
            // Secondary check: AI Estimate vs Claimed (5 rand tolerance)
            const estimateVsClaimedDiff = Math.abs(estimatedDue - discountClaimed);
            if (estimateVsClaimedDiff > 5.00) {
              if (estimatedDue > discountClaimed) {
                status = 'Discount Calc Too Low';   // AI thinks should be higher
              } else {
                status = 'Discount Calc Too High';  // AI thinks should be lower
              }
            } else {
              status = 'Correct';  // Everything matches (green tick)
            }
          }
        }
        
        // Find primary product (Hills brand if available, otherwise first product)
        const primaryProduct = item.products?.find(p => p.brand === 'HILLS') || item.products?.[0] || {
          name: 'No products found',
          brand: 'UNKNOWN',
          category: 'UNKNOWN',
          species: null,
          life_stage: null,
          cost: 0
        };
        
        // Count Hills products
        const hillsProductCount = item.products?.filter(p => p.brand === 'HILLS').length || 0;
        
        // Calculate differences for display purposes (maintain compatibility)
        const hasCalculationError = Math.abs(calculatedDiscount - discountApplied) > 0.01;
        const hasDetectionError = Math.abs(adjustedDetectedDiscount - discountApplied) > 0.01;
        
        // Build combined difference display
        let combinedDifference = [];
        if (hasCalculationError) {
          combinedDifference.push(calculatedDiscount - discountApplied);
        }
        if (hasDetectionError) {
          combinedDifference.push(adjustedDetectedDiscount - discountApplied);
        }
        
        // Use the first difference for backward compatibility, but store both
        let difference = hasCalculationError ? calculatedDiscount - discountApplied : adjustedDetectedDiscount - discountApplied;

        // Derived rule flags & reasons
        const belowMinSpend = typeof item.minSpend === 'number' && typeof item.basketCost === 'number'
          ? item.basketCost < item.minSpend
          : false;
        const exceedsMaxDiscount = typeof item.maxDiscount === 'number' && typeof discountApplied === 'number'
          ? discountApplied > item.maxDiscount
          : false;
        const noHillsItem = hillsProductCount === 0;

        const reasons = [];
        if (Math.abs((discountApplied || 0) - (adjustedDetectedDiscount || 0)) > 5.0) {
          const delta = (discountApplied || 0) - (adjustedDetectedDiscount || 0);
          reasons.push(delta > 0 ? `Claimed > Paid out by R${Math.abs(delta).toFixed(2)}` : `Paid out > Claimed by R${Math.abs(delta).toFixed(2)}`);
        }
        // Only include AI expected reason if it truly differs after VAT correction
        if (Math.abs((calculatedDiscount || 0) - (discountApplied || 0)) > 5.0) {
          reasons.push(`AI expected R${(calculatedDiscount || 0).toFixed(2)}`);
        }
        if (belowMinSpend) reasons.push('Below min spend');
        if (exceedsMaxDiscount) reasons.push('Exceeds max discount');
        if (noHillsItem) reasons.push('No HILLS item');
        
        return {
          // API fields mapped to component expected format
          coupon_metadata_id: item.id,
          actual_code: item.actualCode,
          code: item.code,
          type: item.type,
          state: item.state,
          campaign_name: item.campaignName,
          invoice_uri: item.invoiceUri,
          staff_member: item.staffMember,
          retailer: item.retailer,
          retail_group: item.retailGroup,
          min_spend: item.minSpend,
          max_discount: item.maxDiscount,
          amount: item.amount,
          basket_cost: item.basketCost,
          discount_applied: item.discountApplied,
          calculated_discount: calculatedDiscount,
          detected_discount: adjustedDetectedDiscount,
          basket_size: item.basketSize,
          is_legacy: item.isLegacy,
          is_test: item.isTest,
          is_default_staff_member: item.isDefaultStaffMember,
          is_over_paid: item.isOverPaid,
          is_under_paid: item.isUnderPaid,
          redeemed_at: item.redeemedAt,
          products: item.products || [],
          reasons,
          flags: { belowMinSpend, exceedsMaxDiscount, noHillsItem },
          // Computed fields for UI
          status: status,
          difference: difference,
          combinedDifference: combinedDifference,
          calculationDifference: calculatedDiscount - discountApplied,
          detectionDifference: adjustedDetectedDiscount - discountApplied,
          hasCalculationError: hasCalculationError,
          hasDetectionError: hasDetectionError,
          primaryProduct: primaryProduct,
          hillsProductCount: hillsProductCount
        };
      });
      
      setCouponRedemptions(couponData);
      setCouponPagination({
        pageNumber: result.pageNumber || couponCurrentPage,
        pageSize: result.pageSize || couponPageSize,
        total: result.total || 0,
        pageCount: result.pageCount || 0
      });
      
    } catch (error) {
      console.error('Error fetching coupon redemptions:', error);
      setCouponError(true);
    } finally {
      if (!silent) {
        setCouponLoading(false);
      }
    }
  }, [couponCurrentPage, couponPageSize, dateRange]);

  const handleDrillDown = useCallback((storeName) => {
    setSelectedStore(retailers.find(r => r.name === storeName));
    setDrillDownOpen(true);
    
    // Fetch coupon redemptions for this store
    fetchCouponRedemptions(storeName);
  }, [retailers, fetchCouponRedemptions]);

  const handleRevalidateCouponMetadata = useCallback(async (coupon) => {
    const id = coupon.coupon_metadata_id;
    if (!id) return;
    setRevalidatingCouponId(id);
    try {
      await dashboardAPI.revalidateCouponRedemption(id);
      if (typeof window !== 'undefined' && window.M && typeof window.M.toast === 'function') {
        window.M.toast({ html: 'Coupon metadata has been revalidated.', displayLength: 4000 });
      }
      if (selectedStore?.name) {
        await fetchCouponRedemptions(selectedStore.name, { silent: true });
      }
    } catch (error) {
      const data = error.response?.data;
      let errText = error.message || 'Could not revalidate coupon metadata.';
      if (typeof data?.detail === 'string') {
        errText = data.detail;
      } else if (Array.isArray(data?.errors) && data.errors.length) {
        errText = data.errors.map((e) => e.message || e).join(' ');
      }
      if (typeof window !== 'undefined' && window.M && typeof window.M.toast === 'function') {
        window.M.toast({ html: errText, classes: 'red', displayLength: 6000 });
      }
    } finally {
      setRevalidatingCouponId(null);
    }
  }, [selectedStore, fetchCouponRedemptions]);

  // Register drill-down trigger with parent component
  useEffect(() => {
    if (onRegisterDrillDownTrigger) {
      onRegisterDrillDownTrigger('store', handleDrillDown);
    }
  }, [onRegisterDrillDownTrigger, handleDrillDown]);

  // Handle status filter selection
  const handleStatusFilter = (status) => {
    const newFilter = statusFilter === status ? null : status;
    setStatusFilter(newFilter);
    
    // Reset to first page when filtering
    setCouponCurrentPage(1);
  };

  // Handle derived reason filter selection
  const handleDerivedFilter = (reasonKey) => {
    const newFilter = derivedFilter === reasonKey ? null : reasonKey;
    setDerivedFilter(newFilter);
    setCouponCurrentPage(1);
  };

  // Filter coupon redemptions based on status then derived reason
  const byStatus = statusFilter 
    ? couponRedemptions.filter(coupon => coupon.status === statusFilter)
    : couponRedemptions;

  const allFilteredCouponRedemptions = derivedFilter
    ? byStatus.filter(c => {
        if (derivedFilter === 'belowMinSpend') return c.flags?.belowMinSpend;
        if (derivedFilter === 'exceedsMaxDiscount') return c.flags?.exceedsMaxDiscount;
        if (derivedFilter === 'noHillsItem') return c.flags?.noHillsItem;
        return true;
      })
    : byStatus;

  // Apply client-side pagination when status filter is active (since we fetched all data)
  // Use server-side pagination when no filter (default behavior)
  const filteredCouponRedemptions = statusFilter 
    ? (() => {
        const startIndex = (couponCurrentPage - 1) * couponPageSize;
        const endIndex = startIndex + couponPageSize;
        return allFilteredCouponRedemptions.slice(startIndex, endIndex);
      })()
    : allFilteredCouponRedemptions;

  // Update pagination info - now always using complete dataset
  const effectivePagination = statusFilter 
    ? {
        // When filtering: use filtered data count
        ...couponPagination,
        total: allFilteredCouponRedemptions.length,
        pageCount: Math.ceil(allFilteredCouponRedemptions.length / couponPageSize)
      }
    : {
        // No filter: use complete coupon data count (we always fetch all)
        ...couponPagination,
        total: couponRedemptions.length,
        pageCount: Math.ceil(couponRedemptions.length / couponPageSize)
      };

  // Coupon pagination handlers
  const handleCouponPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= effectivePagination.pageCount) {
      setCouponCurrentPage(newPage);
    }
  };

  const handleCouponPageSizeChange = (size) => {
    setCouponPageSize(size);
    setCouponCurrentPage(1);
  };

  // Handle basket details
  const showBasketDetails = (coupon) => {
    setSelectedBasket(coupon);
    setBasketDetailsOpen(true);
  };

  const closeBasketDetails = () => {
    setBasketDetailsOpen(false);
    setSelectedBasket(null);
  };

  // Handle invoice preview
  const showInvoicePreview = (coupon) => {
    setSelectedInvoice(coupon);
    setInvoicePreviewOpen(true);
  };

  const closeInvoicePreview = () => {
    setInvoicePreviewOpen(false);
    setSelectedInvoice(null);
    // Reset image controls when closing
    setImageRotation(0);
    setImageZoom(1);
    setImagePanX(0);
    setImagePanY(0);
    setIsDragging(false);
  };

  // Image control handlers
  const handleImageRotate = () => {
    setImageRotation(prev => (prev - 90 + 360) % 360);
    // Reset pan when rotating
    setImagePanX(0);
    setImagePanY(0);
  };
  
  const handleImageZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.25, 3));
  };
  
  const handleImageZoomOut = () => {
    setImageZoom(prev => {
      const newZoom = Math.max(prev - 0.25, 0.25);
      // Reset pan when zooming out to fit
      if (newZoom <= 1) {
        setImagePanX(0);
        setImagePanY(0);
      }
      return newZoom;
    });
  };

  // Pan/drag handlers
  const handleImageMouseDown = (e) => {
    if (imageZoom > 1) {
      setIsDragging(true);
      // Store the initial mouse position and current pan position
      setDragStart({
        x: e.clientX,
        y: e.clientY,
        panX: imagePanX,
        panY: imagePanY
      });
      e.preventDefault();
    }
  };

  const handleImageMouseMove = (e) => {
    if (isDragging && imageZoom > 1) {
      // Calculate the mouse movement delta
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      
      // Convert rotation angle to radians
      const angleRad = (imageRotation * Math.PI) / 180;
      
      // Rotate the delta vector by the negative of the image rotation
      // This transforms screen-space movement to image-space movement
      const cosAngle = Math.cos(-angleRad);
      const sinAngle = Math.sin(-angleRad);
      
      const rotatedDeltaX = deltaX * cosAngle - deltaY * sinAngle;
      const rotatedDeltaY = deltaX * sinAngle + deltaY * cosAngle;
      
      // Apply the rotated delta to the original pan position
      setImagePanX(dragStart.panX + rotatedDeltaX);
      setImagePanY(dragStart.panY + rotatedDeltaY);
    }
  };

  const handleImageMouseUp = () => {
    setIsDragging(false);
  };

  const handleImageMouseLeave = () => {
    setIsDragging(false);
  };
  
  const handleImageFullscreen = () => {
    if (selectedInvoice?.invoice_uri) {
      window.open(selectedInvoice.invoice_uri, '_blank');
    }
  };

  // Determine invoice type from URI
  const getInvoiceType = (uri) => {
    if (!uri) return 'unknown';
    const extension = uri.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['jpg', 'jpeg', 'jpe', 'png', 'gif', 'webp'].includes(extension)) return 'image';
    return 'unknown';
  };

  // Update coupon data when pagination changes
  useEffect(() => {
    if (drillDownOpen && selectedStore) {
      fetchCouponRedemptions(selectedStore.name);
    }
  }, [couponCurrentPage, couponPageSize, drillDownOpen, selectedStore, fetchCouponRedemptions, statusFilter]);

  // Top and worst performing stores are now fetched separately via API

  // Chart configurations for top performing stores
  const topStoresChartData = topPerformingStores.length > 0 
    ? [{ 
        name: 'Accuracy (%)', 
        data: topPerformingStores.map(store => isNaN(store.accuracy) ? 0 : (store.accuracy || 0))
      }]
    : [{ 
        name: 'Accuracy (%)', 
        data: [0]
      }];

  const topStoresChartOptions = {
    chart: { 
      type: 'bar', 
      height: 400, 
      background: '#FFFFFF',
      toolbar: {
        show: false
      }
    },
    xaxis: { 
      categories: topPerformingStores.length > 0 ? topPerformingStores.map(store => store.name || 'Unknown') : ['No Data'],
      labels: {
        rotate: -45,
        style: {
          fontSize: '11px'
        }
      }
    },
    colors: [chartColors.primary],
    title: { 
      text: 'Top Performing Vet Stores (Accuracy %)',
      style: {
        fontSize: '16px',
        fontWeight: 'normal'
      }
    },
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + "%";
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"]
      }
    },
    noData: {
      text: 'Loading...',
      align: 'center',
      verticalAlign: 'middle'
    }
  };

  // Chart configurations for worst performing stores
  const worstStoresChartData = worstPerformingStores.length > 0 
    ? [{ 
        name: 'Accuracy (%)', 
        data: worstPerformingStores.map(store => isNaN(store.accuracy) ? 0 : (store.accuracy || 0))
      }]
    : [{ 
        name: 'Accuracy (%)', 
        data: [0]
      }];

  const worstStoresChartOptions = {
    chart: { 
      type: 'bar', 
      height: 400, 
      background: '#FFFFFF',
      toolbar: {
        show: false
      }
    },
    xaxis: { 
      categories: worstPerformingStores.length > 0 ? worstPerformingStores.map(store => store.name || 'Unknown') : ['No Data'],
      labels: {
        rotate: -45,
        style: {
          fontSize: '10px'
        }
      }
    },
    colors: [chartColors.secondary],
    title: { 
      text: 'Worst Performing Vet Stores (Accuracy %)',
      style: {
        fontSize: '16px',
        fontWeight: 'normal'
      }
    },
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + "%";
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"]
      }
    },
    noData: {
      text: 'Loading...',
      align: 'center',
      verticalAlign: 'middle'
    }
  };

  return (
    <>
      {/* Store Based Information */}
      <div className="section-box">
        <div className="section-title">Store Based Information</div>

        <div style={{ marginBottom: '20px', maxWidth: '420px' }}>
          <label
            htmlFor="store-search"
            style={{ fontSize: '12px', color: '#666', marginBottom: '8px', display: 'block' }}
          >
            Search Store
          </label>
          <input
            type="search"
            id="store-search"
            value={storeSearch}
            onChange={(event) => setStoreSearch(event.target.value)}
            placeholder="Search all stores..."
            aria-label="Search all stores"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>
        
        {retailersLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="preloader-wrapper active">
              <div className="spinner-layer spinner-blue-only">
                <div className="circle-clipper left">
                  <div className="circle"></div>
                </div>
                <div className="gap-patch">
                  <div className="circle"></div>
                </div>
                <div className="circle-clipper right">
                  <div className="circle"></div>
                </div>
              </div>
            </div>
            <p>Loading retailer data...</p>
          </div>
        ) : retailersError ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#CE060D' }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
            <p>Error loading retailer data</p>
          </div>
        ) : retailers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <i className="fas fa-info-circle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
            <p>
              {storeSearchQuery
                ? `No stores match “${storeSearchQuery}”`
                : 'No retailer data available for selected date range'}
            </p>
          </div>
        ) : (
          <>
            <table className="striped">
              <thead>
                <tr>
                  <th 
                    onClick={() => handleSort('retailer')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Store Name"
                  >
                    Store Name{getSortIndicator('retailer')}
                  </th>
                  <th 
                    onClick={() => handleSort('total_count')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Claims"
                  >
                    Claims{getSortIndicator('total_count')}
                  </th>
                  <th 
                    onClick={() => handleSort('valid_count')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Correct"
                  >
                    Correct{getSortIndicator('valid_count')}
                  </th>
                  <th 
                    onClick={() => handleSort('accuracy')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Accuracy"
                  >
                    Accuracy{getSortIndicator('accuracy')}
                  </th>
                  <th 
                    onClick={() => handleSort('over_paid_count')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Over Claimed"
                  >
                    Over Claimed{getSortIndicator('over_paid_count')}
                  </th>
                  <th 
                    onClick={() => handleSort('under_paid_count')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Under Paid"
                  >
                    Under Paid{getSortIndicator('under_paid_count')}
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginatedRetailers.map(store => (
                  <tr key={store.name}>
                    <td>{store.name}</td>
                    <td>{formatNumber(store.claims)}</td>
                    <td>{formatNumber(store.correct)}</td>
                    <td>{formatPercentage(store.accuracy)}</td>
                    <td>{formatNumber(store.overClaimed)}</td>
                    <td>{formatNumber(store.underPaid)}</td>
                    <td>
                      <button
                        className="drill-btn"
                        onClick={() => handleDrillDown(store.name)}
                      >
                        Drill Down
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {(effectiveTotalStores > 0) && (
              <div style={{ marginTop: '20px' }}>
                {/* Pagination Info */}
                <div style={{ textAlign: 'center', color: '#666', marginBottom: '15px' }}>
                  <p>Showing {paginatedRetailers.length} of {formatNumber(effectiveTotalStores)} retailers</p>
                  {effectivePageCount > 1 && (
                    <p>Page {currentPage} of {effectivePageCount}</p>
                  )}
                </div>
                
                {/* Pagination Controls */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  {/* Page Size Selector - Button Style */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '15px',
                    backgroundColor: '#f8f9fa',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #e9ecef',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <label style={{ 
                      fontSize: '15px', 
                      color: '#495057',
                      fontWeight: '600',
                      whiteSpace: 'nowrap'
                    }}>
                      Items per page:
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[10, 25, 50, 100].map(size => (
                        <button
                          key={size}
                          onClick={() => {
                            handlePageSizeChange(size);
                          }}
                          style={{
                            padding: '8px 16px',
                            border: pageSize === size ? '2px solid #007bff' : '2px solid #ced4da',
                            borderRadius: '6px',
                            backgroundColor: pageSize === size ? '#007bff' : '#ffffff',
                            color: pageSize === size ? '#ffffff' : '#495057',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease',
                            minWidth: '50px'
                          }}
                          onMouseOver={(e) => {
                            if (pageSize !== size) {
                              e.target.style.backgroundColor = '#e9ecef';
                              e.target.style.borderColor = '#007bff';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (pageSize !== size) {
                              e.target.style.backgroundColor = '#ffffff';
                              e.target.style.borderColor = '#ced4da';
                            }
                          }}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                    <span style={{
                      fontSize: '13px',
                      color: '#6c757d',
                      fontStyle: 'italic'
                    }}>
                      Current: {pageSize}
                    </span>
                  </div>
                  
                  {/* Page Navigation */}
                  {effectivePageCount > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{ 
                          padding: '8px 12px', 
                          border: '1px solid #ddd', 
                          borderRadius: '4px',
                          backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                          color: currentPage === 1 ? '#999' : '#333',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Previous
                      </button>
                      
                      {/* Page Numbers */}
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {/* First page */}
                        {currentPage > 3 && (
                          <>
                            <button 
                              onClick={() => handlePageChange(1)}
                              style={{ 
                                padding: '8px 12px', 
                                border: '1px solid #ddd', 
                                borderRadius: '4px',
                                backgroundColor: '#fff',
                                color: '#333',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              1
                            </button>
                            {currentPage > 4 && <span style={{ padding: '8px 4px', color: '#999' }}>...</span>}
                          </>
                        )}
                        
                        {/* Pages around current */}
                        {[...Array(effectivePageCount)].map((_, index) => {
                          const pageNum = index + 1;
                          if (pageNum >= currentPage - 2 && pageNum <= currentPage + 2) {
                            return (
                              <button 
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                style={{ 
                                  padding: '8px 12px', 
                                  border: '1px solid #ddd', 
                                  borderRadius: '4px',
                                  backgroundColor: pageNum === currentPage ? '#007bff' : '#fff',
                                  color: pageNum === currentPage ? '#fff' : '#333',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: pageNum === currentPage ? 'bold' : 'normal'
                                }}
                              >
                                {pageNum}
                              </button>
                            );
                          }
                          return null;
                        })}
                        
                        {/* Last page */}
                        {currentPage < effectivePageCount - 2 && (
                          <>
                            {currentPage < effectivePageCount - 3 && <span style={{ padding: '8px 4px', color: '#999' }}>...</span>}
                            <button 
                              onClick={() => handlePageChange(effectivePageCount)}
                              style={{ 
                                padding: '8px 12px', 
                                border: '1px solid #ddd', 
                                borderRadius: '4px',
                                backgroundColor: '#fff',
                                color: '#333',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              {effectivePageCount}
                            </button>
                          </>
                        )}
                      </div>
                      
                      <button 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === effectivePageCount}
                        style={{ 
                          padding: '8px 12px', 
                          border: '1px solid #ddd', 
                          borderRadius: '4px',
                          backgroundColor: currentPage === effectivePageCount ? '#f5f5f5' : '#fff',
                          color: currentPage === effectivePageCount ? '#999' : '#333',
                          cursor: currentPage === effectivePageCount ? 'not-allowed' : 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Store Performance Insights */}
      <div className="section-box">
        <div className="section-title">Store Performance Insights</div>
        
        {performanceLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="preloader-wrapper active">
              <div className="spinner-layer spinner-blue-only">
                <div className="circle-clipper left">
                  <div className="circle"></div>
                </div>
                <div className="gap-patch">
                  <div className="circle"></div>
                </div>
                <div className="circle-clipper right">
                  <div className="circle"></div>
                </div>
              </div>
            </div>
            <p>Loading performance insights...</p>
          </div>
        ) : performanceError ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#CE060D' }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
            <p>Error loading performance data</p>
          </div>
        ) : topPerformingStores.length === 0 && worstPerformingStores.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <i className="fas fa-info-circle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
            <p>No performance data available for selected date range</p>
          </div>
        ) : (
          <div className="row">
            <div className="col s12">
              <h5>Top Performing Vet Stores (Accuracy %)</h5>
              {topPerformingStores.length > 0 ? (
                <div className="chart-container">
                  <Chart
                    options={topStoresChartOptions}
                    series={topStoresChartData}
                    type="bar"
                    height={400}
                  />
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p>Not enough data for top performers chart</p>
                </div>
              )}
            </div>
            <div className="col s12">
              <h5>Worst Performing Vet Stores (Accuracy %)</h5>
              {worstPerformingStores.length > 0 ? (
                <div className="chart-container">
                  <Chart
                    options={worstStoresChartOptions}
                    series={worstStoresChartData}
                    type="bar"
                    height={400}
                  />
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p>Not enough data for worst performers chart</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Drill Down Dialog */}
      {drillDownOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#ffffff',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto'
        }}>
          {/* Header with back and close buttons */}
          <div style={{
            position: 'sticky',
            top: 0,
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #e9ecef',
            padding: '20px 30px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 10000
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <button
                onClick={handleCloseDrillDown}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.color = '#333';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#666';
                }}
                title="Back"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <div>
                <h4 style={{ margin: 0, color: '#333', fontSize: '24px', fontWeight: '600' }}>
                  Store Performance Details
                </h4>
                {selectedStore && (
                  <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '16px' }}>
                    {selectedStore.name}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleCloseDrillDown}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#666',
                padding: '10px',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '50px',
                height: '50px'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.color = '#333';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#666';
              }}
              title="Close"
            >
              ×
            </button>
          </div>

          {/* Content Area */}
          <div style={{
            flex: 1,
            padding: '30px',
            backgroundColor: '#f8f9fa'
          }}>
            {selectedStore ? (
              <div>
                {/* Tabs */}
                <div style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '8px',
                  marginBottom: '16px',
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  {[
                    { key: 'performance', label: 'Performance', icon: 'fa-chart-line' },
                    { key: 'redemptions', label: 'Redemptions', icon: 'fa-receipt' },
                    { key: 'campaigns', label: 'Campaigns', icon: 'fa-bullhorn' },
                    { key: 'quality', label: 'Quality', icon: 'fa-check-circle' }
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveStoreTab(tab.key)}
                      style={{
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: activeStoreTab === tab.key ? '2px solid #007bff' : '1px solid #dee2e6',
                        backgroundColor: activeStoreTab === tab.key ? '#e7f1ff' : '#ffffff',
                        color: '#333',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <i className={`fas ${tab.icon}`} style={{ color: activeStoreTab === tab.key ? '#007bff' : '#6c757d' }}></i>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Performance Tab: Store Summary */}
                {activeStoreTab === 'performance' && (
                  <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    padding: '25px',
                    marginBottom: '25px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    border: '1px solid #e9ecef'
                  }}>
                    <h5 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>
                      Store Overview
                    </h5>
                    {(() => {
                      const statusCounts = couponRedemptions.reduce((acc, coupon) => {
                        acc[coupon.status] = (acc[coupon.status] || 0) + 1;
                        return acc;
                      }, {});
                      const actualOverPaid = statusCounts['Over Paid'] || 0;
                      const actualUnderPaid = statusCounts['Under Paid'] || 0;
                      const actualCorrect = statusCounts['Correct'] || 0;

                      const tileBase = {
                        textAlign: 'center',
                        padding: '15px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      };

                      return (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                          <div
                            onClick={() => { setStatusFilter(null); setDerivedFilter(null); setActiveStoreTab('redemptions'); }}
                            style={{
                              ...tileBase,
                              backgroundColor: statusFilter === null ? '#e3f2fd' : '#f8f9fa',
                              border: statusFilter === null ? '2px solid #2196f3' : '2px solid transparent'
                            }}
                            title="Click to view all coupons"
                          >
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff', marginBottom: '5px' }}>
                              {formatNumber(couponRedemptions.length)}
                            </div>
                            <div style={{ color: '#666', fontSize: '14px' }}>Total Claims</div>
                          </div>
                          <div
                            onClick={() => { setStatusFilter('Correct'); setActiveStoreTab('redemptions'); }}
                            style={{
                              ...tileBase,
                              backgroundColor: statusFilter === 'Correct' ? '#e8f5e8' : '#f8f9fa',
                              border: statusFilter === 'Correct' ? '2px solid #4caf50' : '2px solid transparent'
                            }}
                            title="Click to view correct coupons"
                          >
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745', marginBottom: '5px' }}>
                              {formatNumber(actualCorrect)}
                            </div>
                            <div style={{ color: '#666', fontSize: '14px' }}>Correct Claims</div>
                          </div>
                          <div style={{ ...tileBase, cursor: 'default', backgroundColor: '#f8f9fa', border: '2px solid transparent' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8', marginBottom: '5px' }}>
                              {couponRedemptions.length > 0 ? formatPercentage((actualCorrect / couponRedemptions.length) * 100) : '0.0%'}
                            </div>
                            <div style={{ color: '#666', fontSize: '14px' }}>Accuracy</div>
                          </div>
                          <div
                            onClick={actualOverPaid > 0 ? () => { setStatusFilter('Over Paid'); setActiveStoreTab('redemptions'); } : undefined}
                            style={{
                              ...tileBase,
                              backgroundColor: statusFilter === 'Over Paid' ? '#ffeaea' : '#f8f9fa',
                              border: statusFilter === 'Over Paid' ? '2px solid #f44336' : '2px solid transparent',
                              opacity: actualOverPaid === 0 ? 0.6 : 1,
                              cursor: actualOverPaid > 0 ? 'pointer' : 'not-allowed'
                            }}
                            title={actualOverPaid > 0 ? 'Click to view overpaid coupons' : 'No overpaid coupons'}
                          >
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545', marginBottom: '5px' }}>
                              {formatNumber(actualOverPaid)}
                            </div>
                            <div style={{ color: '#666', fontSize: '14px' }}>Over Paid</div>
                          </div>
                          <div
                            onClick={actualUnderPaid > 0 ? () => { setStatusFilter('Under Paid'); setActiveStoreTab('redemptions'); } : undefined}
                            style={{
                              ...tileBase,
                              backgroundColor: statusFilter === 'Under Paid' ? '#fff8e1' : '#f8f9fa',
                              border: statusFilter === 'Under Paid' ? '2px solid #ff9800' : '2px solid transparent',
                              opacity: actualUnderPaid === 0 ? 0.6 : 1,
                              cursor: actualUnderPaid > 0 ? 'pointer' : 'not-allowed'
                            }}
                            title={actualUnderPaid > 0 ? 'Click to view underpaid coupons' : 'No underpaid coupons'}
                          >
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107', marginBottom: '5px' }}>
                              {formatNumber(actualUnderPaid)}
                            </div>
                            <div style={{ color: '#666', fontSize: '14px' }}>Under Paid</div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Daily submissions by campaign (stacked) */}
                    {(() => {
                      if (!couponRedemptions || couponRedemptions.length === 0) return null;

                      // Build dates and per-campaign counts
                      const dateFormatter = (d) => {
                        try { return new Date(d).toISOString().slice(0,10); } catch { return new Date(d).toISOString().slice(0,10); }
                      };
                      const allDatesSet = new Set();
                      const campaignTotals = {};
                      const byDayCampaign = {};
                      couponRedemptions.forEach(c => {
                        const keyDay = dateFormatter(c.redeemed_at);
                        const camp = c.campaign_name || 'Unknown';
                        allDatesSet.add(keyDay);
                        campaignTotals[camp] = (campaignTotals[camp] || 0) + 1;
                        byDayCampaign[keyDay] = byDayCampaign[keyDay] || {};
                        byDayCampaign[keyDay][camp] = (byDayCampaign[keyDay][camp] || 0) + 1;
                      });
                      const categories = Array.from(allDatesSet).sort();

                      // Limit to top 6 campaigns by volume for readability
                      const topCampaigns = Object.entries(campaignTotals)
                        .sort((a,b) => b[1] - a[1])
                        .slice(0, 6)
                        .map(([name]) => name);

                      // Group the rest into 'Other'
                      const seriesNames = [...topCampaigns, ...(Object.keys(campaignTotals).length > topCampaigns.length ? ['Other'] : [])];

                      const series = seriesNames.map(name => ({
                        name,
                        data: categories.map(day => {
                          const dayMap = byDayCampaign[day] || {};
                          if (name === 'Other') {
                            // Sum all non-top campaigns
                            return Object.entries(dayMap)
                              .filter(([camp]) => !topCampaigns.includes(camp))
                              .reduce((sum, [,cnt]) => sum + cnt, 0);
                          }
                          return dayMap[name] || 0;
                        })
                      }));

                      const options = {
                        chart: { type: 'bar', stacked: true, height: 350, background: '#FFFFFF' },
                        xaxis: { categories: categories.map(d => new Date(d).toLocaleDateString()), title: { text: 'Date' }, labels: { rotate: -45 } },
                        yaxis: { title: { text: 'Coupons Submitted' }, min: 0 },
                        legend: { position: 'top' },
                        title: { text: 'Coupons Submitted by Day and Campaign' },
                        plotOptions: { bar: { columnWidth: '60%', borderRadius: 4 } },
                        dataLabels: { enabled: false }
                      };

                      return (
                        <div style={{ marginTop: '20px' }}>
                          <div className="chart-container">
                            <Chart options={options} series={series} type="bar" height={350} />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Redemptions Tab: Coupon Redemptions Table */}
                {activeStoreTab === 'redemptions' && (
                <div style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '25px',
                  marginBottom: '25px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  border: '1px solid #e9ecef'
                }}>
                  <h5 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '20px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <span>Coupon Redemptions</span>
                    {selectedStore?.accuracy !== undefined && (
                      <span style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '12px', backgroundColor: '#e9f7ef', color: '#155724', border: '1px solid #c3e6cb' }} title="Store accuracy">
                        Accuracy: {formatPercentage(selectedStore.accuracy)}
                      </span>
                    )}
                  </h5>
                  
                  {couponLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <div className="preloader-wrapper active">
                        <div className="spinner-layer spinner-blue-only">
                          <div className="circle-clipper left">
                            <div className="circle"></div>
                          </div>
                          <div className="gap-patch">
                            <div className="circle"></div>
                          </div>
                          <div className="circle-clipper right">
                            <div className="circle"></div>
                          </div>
                        </div>
                      </div>
                      <p>Loading coupon redemptions...</p>
                    </div>
                  ) : couponError ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#CE060D' }}>
                      <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                      <p>Error loading coupon redemptions</p>
                    </div>
                  ) : couponRedemptions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <i className="fas fa-info-circle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                      <p>No coupon redemptions found for this store</p>
                    </div>
                  ) : (
                    <>
                      {/* Dynamic Status Filters and Derived Filters + Export */}
                      {couponRedemptions.length > 0 && (
                        <div style={{
                          backgroundColor: '#ffffff',
                          borderRadius: '12px',
                          padding: '20px',
                          marginBottom: '20px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          border: '1px solid #e9ecef'
                        }}>
                          <h6 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                            Filters
                          </h6>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {(() => {
                              // Calculate status counts from actual coupon data
                              const statusCounts = couponRedemptions.reduce((acc, coupon) => {
                                acc[coupon.status] = (acc[coupon.status] || 0) + 1;
                                return acc;
                              }, {});

                              const statusOrder = ['Correct', 'Over Paid', 'Under Paid', 'Discount Calc Too Low', 'Discount Calc Too High', 'Invalid'];
                              
                              const statusButtons = statusOrder.filter(status => statusCounts[status] > 0).map(status => (
                                <button
                                  key={status}
                                  onClick={() => handleStatusFilter(status)}
                                  style={{
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    border: statusFilter === status ? '2px solid' : '2px solid transparent',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    backgroundColor: statusFilter === status 
                                      ? (status === 'Correct' ? '#e8f5e8' :
                                         (status === 'Over Paid' || status === 'Under Paid') ? '#ffeaea' :
                                         (status === 'Discount Calc Too Low' || status === 'Discount Calc Too High') ? '#fff3e0' : '#e9ecef')
                                      : '#f8f9fa',
                                    color: statusFilter === status 
                                      ? (status === 'Correct' ? '#28a745' :
                                         (status === 'Over Paid' || status === 'Under Paid') ? '#721c24' :
                                         (status === 'Discount Calc Too Low' || status === 'Discount Calc Too High') ? '#e65100' : '#6c757d')
                                      : '#495057',
                                    borderColor: statusFilter === status 
                                      ? (status === 'Correct' ? '#28a745' :
                                         (status === 'Over Paid' || status === 'Under Paid') ? '#dc3545' :
                                         (status === 'Discount Calc Too Low' || status === 'Discount Calc Too High') ? '#fd7e14' : '#6c757d')
                                      : 'transparent',
                                    transition: 'all 0.2s ease'
                                  }}
                                  title={`Show ${statusCounts[status]} ${status.toLowerCase()} coupon${statusCounts[status] > 1 ? 's' : ''}`}
                                >
                                  {status} ({statusCounts[status]})
                                </button>
                              ));

                              // Derived reason counts
                              const derived = couponRedemptions.reduce((acc, c) => {
                                if (c.flags?.belowMinSpend) acc.belowMinSpend = (acc.belowMinSpend || 0) + 1;
                                if (c.flags?.exceedsMaxDiscount) acc.exceedsMaxDiscount = (acc.exceedsMaxDiscount || 0) + 1;
                                if (c.flags?.noHillsItem) acc.noHillsItem = (acc.noHillsItem || 0) + 1;
                                return acc;
                              }, {});

                              const derivedButtons = [];
                              if (derived.belowMinSpend) {
                                derivedButtons.push(
                                  <button key="belowMinSpend" onClick={() => handleDerivedFilter('belowMinSpend')}
                                    style={{ padding: '6px 10px', borderRadius: '16px', border: derivedFilter === 'belowMinSpend' ? '2px solid #007bff' : '1px solid #ced4da', backgroundColor: derivedFilter === 'belowMinSpend' ? '#e7f1ff' : '#f8f9fa', color: '#495057', fontSize: '12px', cursor: 'pointer' }}>
                                    Below min spend ({derived.belowMinSpend})
                                  </button>
                                );
                              }
                              if (derived.exceedsMaxDiscount) {
                                derivedButtons.push(
                                  <button key="exceedsMaxDiscount" onClick={() => handleDerivedFilter('exceedsMaxDiscount')}
                                    style={{ padding: '6px 10px', borderRadius: '16px', border: derivedFilter === 'exceedsMaxDiscount' ? '2px solid #dc3545' : '1px solid #ced4da', backgroundColor: derivedFilter === 'exceedsMaxDiscount' ? '#ffeaea' : '#f8f9fa', color: '#495057', fontSize: '12px', cursor: 'pointer' }}>
                                    Exceeds max discount ({derived.exceedsMaxDiscount})
                                  </button>
                                );
                              }
                              if (derived.noHillsItem) {
                                derivedButtons.push(
                                  <button key="noHillsItem" onClick={() => handleDerivedFilter('noHillsItem')}
                                    style={{ padding: '6px 10px', borderRadius: '16px', border: derivedFilter === 'noHillsItem' ? '2px solid #6c757d' : '1px solid #ced4da', backgroundColor: derivedFilter === 'noHillsItem' ? '#e2e3e5' : '#f8f9fa', color: '#495057', fontSize: '12px', cursor: 'pointer' }}>
                                    No HILLS item ({derived.noHillsItem})
                                  </button>
                                );
                              }

                              return (
                                <>
                                  {statusButtons}
                                  {derivedButtons}
                                </>
                              );
                            })()}
                            {(statusFilter || derivedFilter) && (
                              <button
                                onClick={() => { setStatusFilter(null); setDerivedFilter(null); }}
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  border: '2px solid #6c757d',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  backgroundColor: '#6c757d',
                                  color: 'white',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                Clear Filter
                              </button>
                            )}
                            </div>
                            <button
                              onClick={() => {
                                const rows = allFilteredCouponRedemptions.map(c => ({
                                  Date: new Date(c.redeemed_at).toISOString(),
                                  Campaign: c.campaign_name,
                                  BasketTotal: c.basket_cost,
                                  Claimed: c.discount_applied,
                                  'Paid out': c.detected_discount,
                                  AI_Expected: c.calculated_discount,
                                  Status: c.status,
                                  Reasons: (c.reasons || []).join(' | '),
                                  Invoice: c.invoice_uri || ''
                                }));
                                const headers = Object.keys(rows[0] || { Date: '', Campaign: '', BasketTotal: '', Claimed: '', 'Paid out': '', AI_Expected: '', Status: '', Reasons: '', Invoice: '' });
                                const csv = [headers.join(','), ...rows.map(r => headers.map(h => {
                                  const val = r[h];
                                  const str = typeof val === 'string' ? val.replace(/\"/g, '""') : String(val ?? '');
                                  return `"${str}"`;
                                }).join(','))].join('\n');
                                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `store-${selectedStore?.name || 'export'}-coupon-redemptions.csv`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              }}
                              className="drill-btn"
                              title="Export current view to CSV"
                            >
                              Export CSV
                            </button>
                          </div>
                        </div>
                      )}

                      <div style={{ overflowX: 'auto' }}>
                        <table className="striped" style={{ marginBottom: '20px' }}>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Campaign</th>
                              <th>Primary Product</th>
                              <th>Captured Total</th>
                              <th>Discount Claimed</th>
                              <th>Paid out</th>
                              <th>Claimed vs Paid out</th>
                              <th>Status</th>
                              <th>Reasons</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredCouponRedemptions.map((coupon, index) => (
                              <tr key={coupon.coupon_metadata_id || index}>
                                <td title={new Date(coupon.redeemed_at).toLocaleString()}>{formatRelativeDate(coupon.redeemed_at)}</td>
                                <td style={{ fontSize: '12px', maxWidth: '150px' }}>
                                  <div title={coupon.campaign_name}>
                                    {coupon.campaign_name.length > 25 ? 
                                      `${coupon.campaign_name.substring(0, 25)}...` : 
                                      coupon.campaign_name}
                                  </div>
                                </td>
                                <td style={{ fontSize: '12px', maxWidth: '200px' }}>
                                  <div 
                                    title={`${coupon.primaryProduct.name} - Click to see full basket`}
                                    onClick={() => showBasketDetails(coupon)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <strong>{coupon.primaryProduct.brand}</strong><br/>
                                    {coupon.primaryProduct.name.length > 30 ? 
                                      `${coupon.primaryProduct.name.substring(0, 30)}...` : 
                                      coupon.primaryProduct.name}
                                  </div>
                                  {coupon.basket_size > 1 && (
                                    <small 
                                      style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
                                      onClick={() => showBasketDetails(coupon)}
                                    >
                                      +{coupon.basket_size - 1} more item{coupon.basket_size > 2 ? 's' : ''} (click to view)
                                    </small>
                                  )}
                                </td>
                                <td>{formatCurrency(coupon.basket_cost)}</td>
                                <td>
                                  {formatCurrency(coupon.discount_applied)}
                                  {typeof coupon.calculated_discount === 'number' && (
                                    <div style={{ fontSize: '10px', color: '#6c757d' }} title={`AI expected ${formatCurrency(coupon.calculated_discount)}`}>
                                      AI: {formatCurrency(coupon.calculated_discount)}
                                    </div>
                                  )}
                                </td>
                                <td>{formatCurrency(coupon.detected_discount)}</td>
                                <td style={{ 
                                  fontWeight: 'bold'
                                }}>
                                  {(() => {
                                    const discountClaimed = coupon.discount_applied || 0;
                                    const actuallyGiven = coupon.detected_discount || 0;
                                    // Customer perspective: what they got vs what was claimed
                                    const difference = actuallyGiven - discountClaimed;
                                    
                                    if (Math.abs(difference) <= 5.00) {
                                      return <span style={{ color: '#28a745' }}>—</span>; // Within tolerance - green
                                    }
                                    
                                    const color = difference < 0 ? '#dc3545' : '#fd7e14'; // Red for negative (underpaid), orange for positive (overpaid)
                                    const displayValue = difference < 0 
                                      ? `-R${Math.abs(difference).toFixed(2)}`
                                      : `+R${difference.toFixed(2)}`;
                                    
                                    return <span style={{ color }}>{displayValue}</span>;
                                  })()}
                                </td>
                                <td>
                                  {coupon.status === 'Correct' ? (
                                    <i className="fas fa-check-circle" style={{
                                      color: '#28a745',
                                      fontSize: '18px'
                                    }}></i>
                                  ) : (
                                    <span style={{
                                      padding: '4px 8px',
                                      borderRadius: '12px',
                                      fontSize: '12px',
                                      fontWeight: 'bold',
                                      backgroundColor: 
                                        (coupon.status === 'Over Paid' || coupon.status === 'Under Paid') ? '#f8d7da' :
                                        (coupon.status === 'Discount Calc Too Low' || coupon.status === 'Discount Calc Too High') ? '#fff3e0' : '#e2e3e5',
                                      color:
                                        (coupon.status === 'Over Paid' || coupon.status === 'Under Paid') ? '#721c24' :
                                        (coupon.status === 'Discount Calc Too Low' || coupon.status === 'Discount Calc Too High') ? '#e65100' : '#6c757d'
                                    }}>
                                      {coupon.status}
                                    </span>
                                  )}
                                  {coupon.is_test && (
                                    <span style={{
                                      marginLeft: '5px',
                                      padding: '2px 6px',
                                      borderRadius: '8px',
                                      fontSize: '10px',
                                      backgroundColor: '#e2e3e5',
                                      color: '#6c757d'
                                    }}>
                                      TEST
                                    </span>
                                  )}
                                </td>
                                <td style={{ maxWidth: '240px' }}>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {(coupon.reasons || []).slice(0, 4).map((r, i) => (
                                      <span key={i} style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '12px', backgroundColor: '#f1f3f5', color: '#495057', border: '1px solid #dee2e6' }}>{r}</span>
                                    ))}
                                    {(coupon.reasons || []).length > 4 && (
                                      <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '12px', backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeaa7' }}>+{(coupon.reasons || []).length - 4} more</span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <button
                                      type="button"
                                      onClick={() => showInvoicePreview(coupon)}
                                      className="drill-btn"
                                    >
                                      <i className="fas fa-file-invoice" style={{ marginRight: '6px' }} aria-hidden="true" />
                                      View Invoice
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRevalidateCouponMetadata(coupon)}
                                      disabled={!coupon.coupon_metadata_id || revalidatingCouponId === coupon.coupon_metadata_id}
                                      className="drill-btn drill-btn--icon-secondary"
                                      title="Re-fetch and refresh coupon metadata from the server"
                                      aria-label={revalidatingCouponId === coupon.coupon_metadata_id ? 'Refreshing metadata' : 'Refresh coupon metadata'}
                                    >
                                      {revalidatingCouponId === coupon.coupon_metadata_id ? (
                                        <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                                      ) : (
                                        <i className="fas fa-sync-alt" aria-hidden="true" />
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Coupon Pagination Controls */}
                      {effectivePagination.total > 0 && (
                        <div style={{ marginTop: '20px', borderTop: '1px solid #e9ecef', paddingTop: '20px' }}>
                          {/* Pagination Info */}
                          <div style={{ textAlign: 'center', color: '#666', marginBottom: '15px' }}>
                            <p>
                              Showing {filteredCouponRedemptions.length} 
                              {statusFilter ? ` ${statusFilter.toLowerCase()}` : ''} 
                              {' '}of {formatNumber(effectivePagination.total)} coupon redemptions
                              {statusFilter && (
                                <button 
                                  onClick={() => setStatusFilter(null)}
                                  style={{
                                    marginLeft: '10px',
                                    padding: '4px 8px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  Clear Filter
                                </button>
                              )}
                            </p>
                            {effectivePagination.pageCount > 1 && (
                              <p>Page {couponCurrentPage} of {effectivePagination.pageCount}</p>
                            )}
                          </div>
                          
                          {/* Pagination Controls */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                            {/* Page Size Selector */}
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '15px',
                              backgroundColor: '#f8f9fa',
                              padding: '12px 16px',
                              borderRadius: '8px',
                              border: '2px solid #e9ecef',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                              <label style={{ 
                                fontSize: '15px', 
                                color: '#495057',
                                fontWeight: '600',
                                whiteSpace: 'nowrap'
                              }}>
                                Items per page:
                              </label>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                {[10, 25, 50, 100].map(size => (
                                  <button
                                    key={size}
                                    onClick={() => handleCouponPageSizeChange(size)}
                                    style={{
                                      padding: '8px 16px',
                                      border: couponPageSize === size ? '2px solid #007bff' : '2px solid #ced4da',
                                      borderRadius: '6px',
                                      backgroundColor: couponPageSize === size ? '#007bff' : '#ffffff',
                                      color: couponPageSize === size ? '#ffffff' : '#495057',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      transition: 'all 0.2s ease',
                                      minWidth: '50px'
                                    }}
                                  >
                                    {size}
                                  </button>
                                ))}
                              </div>
                            </div>
                            
                            {/* Page Navigation */}
                            {effectivePagination.pageCount > 1 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <button 
                                  onClick={() => handleCouponPageChange(couponCurrentPage - 1)}
                                  disabled={couponCurrentPage === 1}
                                  style={{ 
                                    padding: '8px 12px', 
                                    border: '1px solid #ddd', 
                                    borderRadius: '4px',
                                    backgroundColor: couponCurrentPage === 1 ? '#f5f5f5' : '#fff',
                                    color: couponCurrentPage === 1 ? '#999' : '#333',
                                    cursor: couponCurrentPage === 1 ? 'not-allowed' : 'pointer',
                                    fontSize: '14px'
                                  }}
                                >
                                  Previous
                                </button>
                                
                                {/* Page Numbers */}
                                <div style={{ display: 'flex', gap: '5px' }}>
                                  {/* First page */}
                                  {couponCurrentPage > 3 && (
                                    <>
                                      <button 
                                        onClick={() => handleCouponPageChange(1)}
                                        style={{ 
                                          padding: '8px 12px', 
                                          border: '1px solid #ddd', 
                                          borderRadius: '4px',
                                          backgroundColor: '#fff',
                                          color: '#333',
                                          cursor: 'pointer',
                                          fontSize: '14px'
                                        }}
                                      >
                                        1
                                      </button>
                                      {couponCurrentPage > 4 && <span style={{ padding: '8px 4px', color: '#999' }}>...</span>}
                                    </>
                                  )}
                                  
                                  {/* Pages around current */}
                                  {[...Array(effectivePagination.pageCount)].map((_, index) => {
                                    const pageNum = index + 1;
                                    if (pageNum >= couponCurrentPage - 2 && pageNum <= couponCurrentPage + 2) {
                                      return (
                                        <button 
                                          key={pageNum}
                                          onClick={() => handleCouponPageChange(pageNum)}
                                          style={{ 
                                            padding: '8px 12px', 
                                            border: '1px solid #ddd', 
                                            borderRadius: '4px',
                                            backgroundColor: pageNum === couponCurrentPage ? '#007bff' : '#fff',
                                            color: pageNum === couponCurrentPage ? '#fff' : '#333',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: pageNum === couponCurrentPage ? 'bold' : 'normal'
                                          }}
                                        >
                                          {pageNum}
                                        </button>
                                      );
                                    }
                                    return null;
                                  })}
                                  
                                  {/* Last page */}
                                  {couponCurrentPage < effectivePagination.pageCount - 2 && (
                                    <>
                                      {couponCurrentPage < effectivePagination.pageCount - 3 && <span style={{ padding: '8px 4px', color: '#999' }}>...</span>}
                                      <button 
                                        onClick={() => handleCouponPageChange(effectivePagination.pageCount)}
                                        style={{ 
                                          padding: '8px 12px', 
                                          border: '1px solid #ddd', 
                                          borderRadius: '4px',
                                          backgroundColor: '#fff',
                                          color: '#333',
                                          cursor: 'pointer',
                                          fontSize: '14px'
                                        }}
                                      >
                                        {effectivePagination.pageCount}
                                      </button>
                                    </>
                                  )}
                                </div>
                                
                                <button 
                                  onClick={() => handleCouponPageChange(couponCurrentPage + 1)}
                                  disabled={couponCurrentPage === effectivePagination.pageCount}
                                  style={{ 
                                    padding: '8px 12px', 
                                    border: '1px solid #ddd', 
                                    borderRadius: '4px',
                                    backgroundColor: couponCurrentPage === effectivePagination.pageCount ? '#f5f5f5' : '#fff',
                                    color: couponCurrentPage === effectivePagination.pageCount ? '#999' : '#333',
                                    cursor: couponCurrentPage === effectivePagination.pageCount ? 'not-allowed' : 'pointer',
                                    fontSize: '14px'
                                  }}
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                )}

                {/* Campaigns Tab */}
                {activeStoreTab === 'campaigns' && (
                  <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    padding: '25px',
                    marginBottom: '25px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    border: '1px solid #e9ecef'
                  }}>
                    <h5 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>
                      Campaign Adoption & Performance
                    </h5>
                    {couponLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="preloader-wrapper active">
                          <div className="spinner-layer spinner-blue-only">
                            <div className="circle-clipper left"><div className="circle"></div></div>
                            <div className="gap-patch"><div className="circle"></div></div>
                            <div className="circle-clipper right"><div className="circle"></div></div>
                          </div>
                        </div>
                        <p>Loading campaign metrics...</p>
                      </div>
                    ) : couponError ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#CE060D' }}>
                        <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                        <p>Error loading campaigns</p>
                      </div>
                    ) : couponRedemptions.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <i className="fas fa-info-circle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                        <p>No campaign data for this store</p>
                      </div>
                    ) : (
                      (() => {
                        const total = couponRedemptions.length;
                        const byCampaign = couponRedemptions.reduce((acc, c) => {
                          const key = c.campaign_name || 'Unknown';
                          if (!acc[key]) {
                            acc[key] = { count: 0, correct: 0, invalid: 0, overPaid: 0, underPaid: 0, sumDiscount: 0, lastDate: null };
                          }
                          acc[key].count += 1;
                          if (c.status === 'Correct') acc[key].correct += 1;
                          if (c.status === 'Invalid') acc[key].invalid += 1;
                          if (c.status === 'Over Paid') acc[key].overPaid += 1;
                          if (c.status === 'Under Paid') acc[key].underPaid += 1;
                          acc[key].sumDiscount += (c.discount_applied || 0);
                          const d = new Date(c.redeemed_at);
                          acc[key].lastDate = !acc[key].lastDate || d > acc[key].lastDate ? d : acc[key].lastDate;
                          return acc;
                        }, {});
                        const rows = Object.entries(byCampaign).map(([name, v]) => ({
                          name,
                          transactions: v.count,
                          adoption: total > 0 ? (v.count / total) * 100 : 0,
                          
                          accuracy: v.count > 0 ? (v.correct / v.count) * 100 : 0,
                          avgDiscount: v.count > 0 ? v.sumDiscount / v.count : 0,
                          overPaid: v.overPaid,
                          underPaid: v.underPaid,
                          lastUsed: v.lastDate
                        })).sort((a,b) => b.transactions - a.transactions);

                        const top = rows.slice(0, 10);
                        const barOptions = {
                          chart: { type: 'bar', height: 350, background: '#FFFFFF' },
                          xaxis: { categories: top.map(r => r.name), title: { text: 'Campaign' }, labels: { rotate: -30 } },
                          yaxis: { title: { text: 'Transactions' }, min: 0 },
                          colors: ['#007bff'],
                          title: { text: 'Top Campaigns by Transactions' },
                          plotOptions: { bar: { columnWidth: '55%', borderRadius: 4 } },
                          dataLabels: { enabled: false }
                        };
                        const barSeries = [{ name: 'Transactions', data: top.map(r => r.transactions) }];

                        return (
                          <>
                            <div className="chart-container" style={{ marginBottom: '20px' }}>
                              <Chart options={barOptions} series={barSeries} type="bar" height={350} />
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                              <table className="striped" style={{ minWidth: '900px' }}>
                                <thead>
                                  <tr>
                                    <th>Campaign</th>
                                    <th style={{ textAlign: 'right' }}>Transactions</th>
                                    <th style={{ textAlign: 'right' }}>Adoption</th>
                                    
                                    <th style={{ textAlign: 'right' }}>Accuracy</th>
                                    <th style={{ textAlign: 'right' }}>Avg Discount</th>
                                    <th style={{ textAlign: 'right' }}>Over Paid</th>
                                    <th style={{ textAlign: 'right' }}>Under Paid</th>
                                    <th style={{ textAlign: 'right' }}>Last Used</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map((r, i) => (
                                    <tr key={i}>
                                      <td style={{ maxWidth: '260px' }} title={r.name}>{r.name.length > 40 ? `${r.name.substring(0,40)}...` : r.name}</td>
                                      <td style={{ textAlign: 'right' }}>{r.transactions.toLocaleString()}</td>
                                      <td style={{ textAlign: 'right' }}>{r.adoption.toFixed(1)}%</td>
                                      
                                      <td style={{ textAlign: 'right' }}>{r.accuracy.toFixed(1)}%</td>
                                      <td style={{ textAlign: 'right' }}>R{r.avgDiscount.toFixed(2)}</td>
                                      <td style={{ textAlign: 'right', color: '#dc3545' }}>{r.overPaid}</td>
                                      <td style={{ textAlign: 'right', color: '#fd7e14' }}>{r.underPaid}</td>
                                      <td style={{ textAlign: 'right' }}>{r.lastUsed ? new Date(r.lastUsed).toLocaleDateString() : '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        );
                      })()
                    )}
                  </div>
                )}

                {/* Quality Tab: Daily Accuracy Trend */}
                {activeStoreTab === 'quality' && (
                  <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    padding: '25px',
                    marginBottom: '25px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    border: '1px solid #e9ecef'
                  }}>
                    <h5 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>
                      Daily Accuracy Trend
                    </h5>
                    {couponLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="preloader-wrapper active">
                          <div className="spinner-layer spinner-blue-only">
                            <div className="circle-clipper left"><div className="circle"></div></div>
                            <div className="gap-patch"><div className="circle"></div></div>
                            <div className="circle-clipper right"><div className="circle"></div></div>
                          </div>
                        </div>
                        <p>Loading quality metrics...</p>
                      </div>
                    ) : couponRedemptions.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <i className="fas fa-info-circle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                        <p>No data to plot</p>
                      </div>
                    ) : (
                      (() => {
                        const byDay = couponRedemptions.reduce((acc, c) => {
                          const d = new Date(c.redeemed_at);
                          const key = isNaN(d.getTime()) ? 'Invalid' : d.toISOString().slice(0,10);
                          if (!acc[key]) acc[key] = { total: 0, correct: 0 };
                          acc[key].total += 1;
                          if (c.status === 'Correct') acc[key].correct += 1;
                          return acc;
                        }, {});
                        const days = Object.keys(byDay).filter(k => k !== 'Invalid').sort();
                        const categories = days.map(d => new Date(d).toLocaleDateString());
                        const seriesData = days.map(d => byDay[d].total ? parseFloat(((byDay[d].correct / byDay[d].total) * 100).toFixed(1)) : 0);
                        const options = {
                          chart: { type: 'line', height: 300, background: '#FFFFFF' },
                          xaxis: { categories, title: { text: 'Date' }, labels: { rotate: -45 } },
                          yaxis: { title: { text: 'Accuracy (%)' }, min: 0, max: 100 },
                          colors: ['#17a2b8'],
                          stroke: { curve: 'smooth', width: 3 },
                          markers: { size: 3 },
                          title: { text: 'Accuracy by Day' }
                        };
                        const series = [{ name: 'Accuracy (%)', data: seriesData }];
                        return (
                          <div className="chart-container">
                            <Chart options={options} series={series} type="line" height={300} />
                          </div>
                        );
                      })()
                    )}
                  </div>
                )}


              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                <p>No store data available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Basket Details Modal */}
      {basketDetailsOpen && selectedBasket && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '25px',
            width: '900px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
            border: '1px solid #e9ecef'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '2px solid #e9ecef',
              paddingBottom: '15px'
            }}>
              <div>
                <h5 style={{ margin: 0, color: '#333', fontSize: '18px', fontWeight: '600' }}>
                  Basket Details
                </h5>
                <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                  Coupon: {selectedBasket.actual_code} | {selectedBasket.campaign_name}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={() => showInvoicePreview(selectedBasket)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #28a745',
                    borderRadius: '6px',
                    backgroundColor: '#28a745',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#1e7e34';
                    e.target.style.borderColor = '#1e7e34';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#28a745';
                    e.target.style.borderColor = '#28a745';
                  }}
                  title="View invoice"
                >
                  <i className="fas fa-receipt" style={{ marginRight: '5px' }}></i>
                  Invoice
                </button>
                <button
                  onClick={closeBasketDetails}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '5px',
                    borderRadius: '50%',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.color = '#333';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#666';
                  }}
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Product</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Brand</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Species</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Life Stage</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Category</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBasket.products.map((product, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #e9ecef' }}>
                        <td style={{ padding: '10px', fontSize: '12px', maxWidth: '250px' }}>
                          <div title={product.name}>
                            {product.name.length > 40 ? `${product.name.substring(0, 40)}...` : product.name}
                          </div>
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px', fontWeight: 'bold' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            backgroundColor: product.brand === 'HILLS' ? '#d4edda' : '#e2e3e5',
                            color: product.brand === 'HILLS' ? '#155724' : '#6c757d'
                          }}>
                            {product.brand}
                          </span>
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px' }}>
                          {product.species ? (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '6px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              backgroundColor: product.species === 'DOG' ? '#e3f2fd' : product.species === 'CAT' ? '#fce4ec' : '#f3e5f5',
                              color: product.species === 'DOG' ? '#1565c0' : product.species === 'CAT' ? '#c2185b' : '#7b1fa2'
                            }}>
                              {product.species}
                            </span>
                          ) : (
                            <span style={{ color: '#999', fontSize: '11px' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px' }}>
                          {product.life_stage ? (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '6px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              backgroundColor: '#fff3e0',
                              color: '#e65100'
                            }}>
                              {product.life_stage}
                            </span>
                          ) : (
                            <span style={{ color: '#999', fontSize: '11px' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px' }}>{product.category}</td>
                        <td style={{ padding: '10px', fontSize: '12px', textAlign: 'right', fontWeight: 'bold' }}>
                          R{product.cost.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td colSpan="5" style={{ padding: '10px', fontSize: '14px', textAlign: 'right' }}>
                        Total Basket Cost:
                      </td>
                      <td style={{ padding: '10px', fontSize: '14px', textAlign: 'right' }}>
                        R{selectedBasket.basket_cost.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '15px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#007bff' }}>
                  {selectedBasket.basket_size}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Items</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#28a745' }}>
                  {selectedBasket.hillsProductCount}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Hills Products</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#28a745' }}>
                  R{selectedBasket.products.filter(p => p.brand === 'HILLS').reduce((sum, p) => sum + p.cost, 0).toFixed(2)}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Hills Total</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#17a2b8' }}>
                  R{selectedBasket.discount_applied.toFixed(2)}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Discount Applied</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: (!selectedBasket.hasCalculationError && !selectedBasket.hasDetectionError) ? '#666' : (selectedBasket.calculationDifference > 0.01 || selectedBasket.detectionDifference > 0.01) ? '#dc3545' : '#28a745' }}>
                          {(!selectedBasket.hasCalculationError && !selectedBasket.hasDetectionError) ? '—' : 
                           [
                             selectedBasket.hasCalculationError && {
                               icon: <i className="fas fa-file-alt" style={{ marginRight: '2px', color: '#555555', fontSize: '12px' }}></i>,
                               amount: selectedBasket.calculationDifference > 0 ? `+R${selectedBasket.calculationDifference.toFixed(2)}` : `-R${Math.abs(selectedBasket.calculationDifference).toFixed(2)}`
                             },
                             selectedBasket.hasDetectionError && {
                               icon: <i className="fas fa-user" style={{ marginRight: '2px', color: '#555555', fontSize: '12px' }}></i>,
                               amount: selectedBasket.detectionDifference > 0 ? `+R${selectedBasket.detectionDifference.toFixed(2)}` : `-R${Math.abs(selectedBasket.detectionDifference).toFixed(2)}`
                             }
                           ].filter(Boolean).map((item, index) => (
                             <span key={index}>{item.icon}{item.amount}</span>
                           )).reduce((prev, curr, index) => 
                             index === 0 ? [curr] : [...prev, ' | ', curr], []
                           )}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Difference</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Preview Modal */}
      {invoicePreviewOpen && selectedInvoice && (
        <div 
          onClick={closeInvoicePreview}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '0',
            width: '1000px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
            border: '1px solid #e9ecef',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 25px',
              height: '80px',
              borderBottom: '2px solid #555555',
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <button
                  onClick={closeInvoicePreview}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '8px',
                    borderRadius: '50%',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.color = '#333';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#666';
                  }}
                  title="Back"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <div>
                  <h5 style={{ margin: 0, color: '#333', fontSize: '18px', fontWeight: '600' }}>
                    {selectedInvoice.campaign_name}
                  </h5>
                  <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                    Coupon: {selectedInvoice.actual_code}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={() => window.open(selectedInvoice.invoice_uri, '_blank')}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #007bff',
                    borderRadius: '6px',
                    backgroundColor: '#007bff',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#0056b3';
                    e.target.style.borderColor = '#0056b3';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#007bff';
                    e.target.style.borderColor = '#007bff';
                  }}
                  title="Open in new tab"
                >
                  <i className="fas fa-external-link-alt" style={{ marginRight: '5px' }}></i>
                  Open
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={{
              position: 'relative',
              height: 'calc(90vh - 160px)', // Account for header height (80px) and footer + padding
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#f8f9fa',
              overflow: 'auto'
            }}>
              {(() => {
                const invoiceType = getInvoiceType(selectedInvoice.invoice_uri);
                
                if (invoiceType === 'pdf') {
                  return (
                    <iframe
                      src={`${selectedInvoice.invoice_uri}#toolbar=0`}
                      style={{
                        width: '100%',
                        height: '70vh',
                        border: 'none',
                        backgroundColor: '#ffffff'
                      }}
                      title="Invoice PDF"
                    />
                  );
                } else if (invoiceType === 'image') {
                  return (
                    <div style={{ position: 'relative', width: '100%', height: '70vh', display: 'flex', flexDirection: 'column' }}>
                      {/* Image Controls */}
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        zIndex: 1000,
                        display: 'flex',
                        gap: '5px',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        borderRadius: '8px',
                        padding: '8px'
                      }}>
                        <button
                          onClick={handleImageRotate}
                          style={{
                            background: 'rgba(255,255,255,0.9)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            height: '32px'
                          }}
                          title="Rotate 90°"
                        >
                          <i className="fas fa-undo"></i>
                        </button>
                        <button
                          onClick={handleImageZoomOut}
                          style={{
                            background: 'rgba(255,255,255,0.9)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            height: '32px'
                          }}
                          title="Zoom Out"
                        >
                          <i className="fas fa-search-minus"></i>
                        </button>
                        <span style={{
                          background: 'rgba(255,255,255,0.9)',
                          borderRadius: '4px',
                          padding: '8px',
                          fontSize: '12px',
                          color: '#333',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: '50px',
                          justifyContent: 'center'
                        }}>
                          {Math.round(imageZoom * 100)}%
                        </span>
                        <button
                          onClick={handleImageZoomIn}
                          style={{
                            background: 'rgba(255,255,255,0.9)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            height: '32px'
                          }}
                          title="Zoom In"
                        >
                          <i className="fas fa-search-plus"></i>
                        </button>
                        <button
                          onClick={handleImageFullscreen}
                          style={{
                            background: 'rgba(255,255,255,0.9)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            height: '32px'
                          }}
                          title="Open Fullscreen"
                        >
                          <i className="fas fa-expand"></i>
                        </button>
                      </div>
                      
                      {/* Image Container */}
                      <div 
                        style={{ 
                          flex: 1, 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          overflow: imageZoom > 1 ? 'hidden' : 'auto',
                          backgroundColor: '#f8f9fa',
                          cursor: imageZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                          position: 'relative'
                        }}
                        onMouseMove={handleImageMouseMove}
                        onMouseUp={handleImageMouseUp}
                        onMouseLeave={handleImageMouseLeave}
                      >
                        <img
                          src={selectedInvoice.invoice_uri}
                          alt="Invoice"
                          draggable={false}
                          style={{
                            maxWidth: imageZoom > 1 ? 'none' : '100%',
                            maxHeight: imageZoom > 1 ? 'none' : '100%',
                            width: imageZoom > 1 ? `${imageZoom * 100}%` : 'auto',
                            height: imageZoom > 1 ? `${imageZoom * 100}%` : 'auto',
                            objectFit: 'contain',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            transform: `rotate(${imageRotation}deg) translate(${imageZoom > 1 ? imagePanX : 0}px, ${imageZoom > 1 ? imagePanY : 0}px)`,
                            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                            userSelect: 'none'
                          }}
                          onMouseDown={handleImageMouseDown}
                          onError={(e) => {
                            // Hide the image and show error message
                            e.target.style.display = 'none';
                            const errorDiv = e.target.parentElement.parentElement.querySelector('.image-error-fallback');
                            if (errorDiv) {
                              errorDiv.style.display = 'block';
                            }
                          }}
                        />
                      </div>
                      
                      <div 
                        className="image-error-fallback"
                        style={{
                          display: 'none',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textAlign: 'center',
                          padding: '60px 40px',
                          color: '#666',
                          backgroundColor: '#fff',
                          borderRadius: '8px',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                        }}
                      >
                        <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', marginBottom: '20px', color: '#ffc107' }}></i>
                        <h6 style={{ margin: '0 0 15px 0', color: '#333' }}>
                          Failed to Load Invoice Image
                        </h6>
                        <p style={{ margin: '0 0 20px 0', fontSize: '14px' }}>
                          There was an error loading the invoice image.
                        </p>
                        <button
                          onClick={() => window.open(selectedInvoice.invoice_uri, '_blank')}
                          style={{
                            padding: '10px 20px',
                            border: '1px solid #007bff',
                            borderRadius: '6px',
                            backgroundColor: '#007bff',
                            color: '#ffffff',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          <i className="fas fa-external-link-alt" style={{ marginRight: '8px' }}></i>
                          Open in New Tab
                        </button>
                      </div>
                    </div>
                  );
                } else {
                  // Fallback for unknown types
                  return (
                    <div style={{
                      textAlign: 'center',
                      padding: '60px 40px',
                      color: '#666'
                    }}>
                      <i className="fas fa-file-alt" style={{ fontSize: '48px', marginBottom: '20px', color: '#007bff' }}></i>
                      <h6 style={{ margin: '0 0 15px 0', color: '#333' }}>
                        Invoice Preview Not Available
                      </h6>
                      <p style={{ margin: '0 0 20px 0', fontSize: '14px' }}>
                        The invoice format cannot be previewed in browser.
                      </p>
                      <button
                        onClick={() => window.open(selectedInvoice.invoice_uri, '_blank')}
                        style={{
                          padding: '10px 20px',
                          border: '1px solid #007bff',
                          borderRadius: '6px',
                          backgroundColor: '#007bff',
                          color: '#ffffff',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        <i className="fas fa-download" style={{ marginRight: '8px' }}></i>
                        Download Invoice
                      </button>
                    </div>
                  );
                }
              })()}


            </div>

            {/* Footer with invoice details */}
            <div style={{
              padding: '11px 25px',
              borderTop: '1px solid #e9ecef',
              backgroundColor: '#f8f9fa',
              height: '120px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '25px',
                padding: '14px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}>
                {/* CAPTURED BY VET Section */}
                <div>
                  <h6 style={{ 
                    margin: '0 0 6px 0', 
                    fontSize: '14px', 
                    fontWeight: 'bold', 
                    color: '#333',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    CAPTURED BY VET:
                  </h6>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#666' }}>Total Spend:</span>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#007bff' }}>
                        R{selectedInvoice.basket_cost.toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#666' }}>Discount claimed:</span>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#007bff' }}>
                        R{selectedInvoice.discount_applied.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* DISCOUNT Section */}
                <div>
                  <h6 style={{ 
                    margin: '0 0 6px 0', 
                    fontSize: '14px', 
                    fontWeight: 'bold', 
                    color: '#333',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    PROCESSED BY AI:
                  </h6>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#666', width: '140px' }}>Estimated Due (ai):</span>
                      <span style={{ 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: Math.abs(selectedInvoice.discount_applied - selectedInvoice.calculated_discount) < 0.01 ? '#28a745' : '#ff8c00',
                        paddingLeft: '80px'
                      }}>
                        <i className="fas fa-file-alt" style={{ marginRight: '4px', color: '#555555' }}></i>
                        R{selectedInvoice.calculated_discount.toFixed(2)}
                        {Math.abs(selectedInvoice.discount_applied - selectedInvoice.calculated_discount) >= 0.01 && 
                          <span>
                            {' '}
                            ({selectedInvoice.calculated_discount - selectedInvoice.discount_applied >= 0 ? '+' : ''}{(selectedInvoice.calculated_discount - selectedInvoice.discount_applied).toFixed(2)})
                          </span>
                        }
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#666', width: '140px' }}>Actually Given:</span>
                      <span>
                        <span style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold', 
                          color: '#007bff',
                          paddingLeft: '80px'
                        }}>
                          <i className="fas fa-user" style={{ marginRight: '4px', color: '#555555' }}></i>
                          R{selectedInvoice.detected_discount.toFixed(2)}
                        </span>
                        {Math.abs(selectedInvoice.discount_applied - selectedInvoice.detected_discount) >= 0.01 && 
                          <span style={{ 
                            fontSize: '16px', 
                            fontWeight: 'bold', 
                            color: '#ff8c00'
                          }}>
                            {' '}
                            ({selectedInvoice.detected_discount - selectedInvoice.discount_applied >= 0 ? '+' : ''}{(selectedInvoice.detected_discount - selectedInvoice.discount_applied).toFixed(2)})
                          </span>
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StorePerformance; 